import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import AuthShell from "@/pages/auth/AuthShell";
import { Field, TextInput, SpecialistsEditor } from "@/components/common/Forms";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, useFetch } from "@/lib/api";

const STEPS = ["Il centro", "Referente", "Il team", "Conferma"];
const EMPTY = {
  company_name: "", center_name: "", vat_number: "", tax_code: "", sdi_code: "", pec: "", address: "", zip_code: "",
  city: "", province: "", owner_first_name: "", owner_last_name: "", phone: "", email: "", password: "",
  specialists: [{ first_name: "", last_name: "" }], registrant_role: "titolare", declared_protocols: [], instagram: "", website: "", privacy_accepted: false,
};
const REQUIRED = [
  ["company_name", "center_name", "vat_number", "tax_code", "address", "zip_code", "city", "province"],
  ["owner_first_name", "owner_last_name", "phone", "email", "password"],
  [],
  [],
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validate(step, f, confirm) {
  if (REQUIRED[step].some((k) => !String(f[k]).trim())) return "Compila tutti i campi obbligatori";
  if (step === 0 && !f.sdi_code.trim() && !f.pec.trim()) return "Indica il codice SDI oppure la PEC";
  if (step === 1 && !EMAIL_RE.test(f.email.trim())) return "L'email non sembra corretta: controlla che non ci siano spazi o errori";
  if (step === 1 && f.password.length < 8) return "La password deve avere almeno 8 caratteri";
  if (step === 1 && f.password !== confirm) return "Le due password non coincidono";
  if (step === 2 && f.registrant_role === "titolare" && f.specialists.some((s) => !s.first_name.trim() !== !s.last_name.trim())) return "Completa nome e cognome di ogni specialist (o rimuovi le righe vuote)";
  if (step === 3 && !f.privacy_accepted) return "Per proseguire è necessario accettare l'informativa privacy";
  return null;
}

export default function Register() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const { data: opts } = useFetch("/public/registration-options");
  const [step, setStep] = useState(0);
  const [f, setF] = useState(EMPTY);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => {
    setError("");
    setF({ ...f, [k]: e.target.value });
  };
  const inp = (k, label, props = {}) => (
    <Field label={label} className={props.className}><TextInput data-testid={`register-${k.replace(/_/g, "-")}-input`} value={f[k]} onChange={set(k)} {...props} className="" /></Field>
  );

  const next = async () => {
    const err = validate(step, f, confirm);
    setError(err || "");
    if (err) return toast.error(err);
    if (step < 3) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return setStep(step + 1);
    }
    setBusy(true);
    try {
      const owner = f.registrant_role === "titolare";
      const body = { ...f, email: f.email.trim().toLowerCase(), specialists: owner ? f.specialists.filter((s) => s.first_name.trim() || s.last_name.trim()) : [] };
      const r = await api.post("/auth/register-center", body);
      setUser(r.data);
      nav("/", { replace: true });
    } catch (e) {
      const msg = e?.response ? errMsg(e) : "Connessione non riuscita. Controlla la rete e riprova.";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };
  const toggleProtocol = (id) =>
    setF({ ...f, declared_protocols: f.declared_protocols.includes(id) ? f.declared_protocols.filter((x) => x !== id) : [...f.declared_protocols, id] });

  return (
    <AuthShell wide>
      <p className="eyebrow mb-4">Richiesta di iscrizione · {step + 1} di 4</p>
      <h1 className="text-4xl sm:text-5xl font-light leading-tight">{STEPS[step]}</h1>
      <div className="flex gap-2 mt-6 mb-10" data-testid="register-progress">
        {STEPS.map((s, i) => <span key={s} className={`h-px flex-1 ${i <= step ? "bg-gold" : "bg-warm/50"}`} />)}
      </div>

      <div className="space-y-5" data-testid={`register-step-${step}`}>
        {step === 0 && (
          <>
            {inp("center_name", "Nome del centro *")}
            {inp("company_name", "Ragione sociale *")}
            <div className="grid sm:grid-cols-2 gap-5">{inp("vat_number", "Partita IVA *")}{inp("tax_code", "Codice fiscale *")}</div>
            <div className="grid sm:grid-cols-2 gap-5">{inp("sdi_code", "Codice SDI")}{inp("pec", "PEC", { type: "email" })}</div>
            <p className="text-xs text-warm-text -mt-2">Basta uno dei due: codice SDI oppure PEC.</p>
            {inp("address", "Indirizzo *")}
            <div className="grid grid-cols-[1fr_2fr_1fr] gap-3 sm:gap-5">{inp("zip_code", "CAP *")}{inp("city", "Città *")}{inp("province", "Prov. *", { maxLength: 2 })}</div>
          </>
        )}
        {step === 1 && (
          <>
            <div>
              <p className="field-label">Chi sta compilando la richiesta? *</p>
              <div className="grid grid-cols-2 gap-3">
                {[["titolare", "Sono il titolare"], ["dipendente", "Sono un dipendente"]].map(([v, l]) => (
                  <button key={v} type="button" data-testid={`register-role-${v}`} onClick={() => setF({ ...f, registrant_role: v })}
                    className={`h-12 rounded-xl border text-sm transition-[background-color,border-color,color] duration-300 ${f.registrant_role === v ? "border-gold bg-gold/15 text-gold" : "border-white/10 text-warm-text hover:border-gold/50"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">{f.registrant_role === "titolare"
              ? <>{inp("owner_first_name", "Nome del titolare *")}{inp("owner_last_name", "Cognome del titolare *")}</>
              : <>{inp("owner_first_name", "Il tuo nome *")}{inp("owner_last_name", "Il tuo cognome *")}</>}</div>
            {inp("phone", "Telefono *", { type: "tel" })}
            {inp("email", "Email *", { type: "email", autoComplete: "email" })}
            <p className="text-xs text-warm-text -mt-2">Sarà l'email con cui accederai all'app.</p>
            <div className="grid sm:grid-cols-2 gap-5">
              {inp("password", "Password *", { type: "password", autoComplete: "new-password" })}
              <Field label="Ripeti la password *"><TextInput data-testid="register-password-confirm-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} /></Field>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            {f.registrant_role === "titolare" ? (
              <div>
                <p className="field-label">Beauty specialist che operano nel centro</p>
                <SpecialistsEditor value={f.specialists} onChange={(v) => setF({ ...f, specialists: v })} testid="register-specialists" />
              </div>
            ) : (
              <p data-testid="register-specialists-owner-only" className="rounded-xl border border-gold/25 bg-gold/[0.06] px-4 py-4 text-sm text-warm-text leading-relaxed">
                Le beauty specialist del centro vengono inserite solo dal titolare. Potrà aggiungerle in seguito.
              </p>
            )}
            <div className="hairline pt-6">
              <p className="field-label">Protocolli in cui siete formate</p>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                {(opts?.protocols || []).map((p) => (
                  <label key={p.id} className="flex items-center gap-3 lux-card px-4 py-3 cursor-pointer text-sm">
                    <Checkbox data-testid={`register-protocol-${p.id}`} checked={f.declared_protocols.includes(p.id)} onCheckedChange={() => toggleProtocol(p.id)} />
                    {p.name}
                  </label>
                ))}
              </div>
            </div>
          </>
        )}
        {step === 3 && (
          <>
            <div className="grid sm:grid-cols-2 gap-5">
              {inp("instagram", "Instagram (facoltativo)", { placeholder: "@iltuocentro" })}
              {inp("website", "Sito web (facoltativo)", { placeholder: "www.iltuocentro.it" })}
            </div>
            <div className="hairline pt-6">
              <p className="field-label">Informativa privacy</p>
              <div data-testid="privacy-text" className="lux-card p-5 max-h-48 overflow-auto text-sm leading-relaxed whitespace-pre-wrap text-warm-text">{opts?.privacy_text}</div>
              <label className="flex items-start gap-3 mt-4 text-sm cursor-pointer">
                <Checkbox data-testid="register-privacy-checkbox" checked={f.privacy_accepted} onCheckedChange={(v) => setF({ ...f, privacy_accepted: !!v })} className="mt-0.5" />
                Ho letto l'informativa e acconsento al trattamento dei dati *
              </label>
            </div>
          </>
        )}
      </div>

      {error && (
        <p data-testid="register-error" role="alert" className="mt-10 border-l-2 border-destructive bg-ivory-deep px-4 py-3 text-sm text-destructive">{error}</p>
      )}
      <div className="flex items-center justify-between gap-4 mt-8">
        {step > 0 ? (
          <button data-testid="register-back-btn" onClick={() => { setError(""); setStep(step - 1); }} className="btn-link">Indietro</button>
        ) : (
          <Link to="/accedi" className="btn-link">Hai già un account? Accedi</Link>
        )}
        <button data-testid="register-next-btn" onClick={next} disabled={busy} className="btn-gold">
          {step < 3 ? "Continua" : busy ? "Invio…" : "Invia la richiesta"}
        </button>
      </div>
    </AuthShell>
  );
}
